"use client";

import { useState, useMemo, type ReactNode } from "react";
import type { GroupsPackage, GroupsPackageTag } from "@/lib/groups/packages";
import type { SelectOption } from "@/app/(main)/components/SearchableSelect";
import SearchableSelect from "@/app/(main)/components/SearchableSelect";
import PackageListCard from "./PackageListCard";
import PackageGridCard from "./PackageGridCard";
import styles from "./packages-gallery.module.css";

type ViewMode = "list" | "grid";

interface PackagesGalleryProps {
  packages: GroupsPackage[];
  /** Server-rendered section heading, placed on the start of the toolbar row. */
  header?: ReactNode;
}

const VIEWS: { mode: ViewMode; label: string; icon: string }[] = [
  { mode: "list", label: "List", icon: "fa-list" },
  { mode: "grid", label: "Grid", icon: "fa-grip" },
];

/** Collect distinct `{id,name}` tags across all packages → sorted select options. */
function toOptions(
  packages: GroupsPackage[],
  pick: (p: GroupsPackage) => GroupsPackageTag[],
): SelectOption[] {
  const byId = new Map<string, string>();
  for (const p of packages) {
    for (const tag of pick(p)) {
      if (tag.id && tag.name && !byId.has(tag.id)) byId.set(tag.id, tag.name);
    }
  }
  return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * Client wrapper for the packages section: destination + theme filters, a
 * list/grid toggle, and the cards.
 *
 * Everything is derived from the one server-rendered `packages` list — each
 * package carries its `countries` and `themes`, so the dropdown options only
 * list destinations/themes that are actually used (no empty results for a
 * single filter), and filtering is an instant client-side `useMemo`, no API
 * round-trips. The initial unfiltered list is server-rendered for SEO.
 *
 * Combining two filters can still yield nothing (e.g. a destination that has no
 * package in the chosen theme); the empty state handles that gracefully.
 */
export default function PackagesGallery({
  packages,
  header,
}: PackagesGalleryProps) {
  const [view, setView] = useState<ViewMode>("grid");
  const [destination, setDestination] = useState("");
  const [theme, setTheme] = useState("");

  const destinationOptions = useMemo(
    () => toOptions(packages, (p) => p.countries),
    [packages],
  );
  const themeOptions = useMemo(
    () => toOptions(packages, (p) => p.themes),
    [packages],
  );

  const filtered = useMemo(
    () =>
      packages.filter(
        (p) =>
          (!destination || p.countries.some((c) => c.id === destination)) &&
          (!theme || p.themes.some((t) => t.id === theme)),
      ),
    [packages, destination, theme],
  );

  const hasFilters = Boolean(destination || theme);
  const clearFilters = () => {
    setDestination("");
    setTheme("");
  };

  // Re-key the results on each filter/view change so the fade-in replays.
  const resultsKey = `${view}-${destination}-${theme}`;

  return (
    <>
      {header ? (
        <div className={styles.headerRow}>
          <div className={styles.headerText}>{header}</div>
        </div>
      ) : null}

      {/* ── Filter bar: filters on the start, view toggle on the end ── */}
      <div className={styles.filterBar}>
        {destinationOptions.length > 0 || themeOptions.length > 0 ? (
          <div className={styles.filters}>
            {destinationOptions.length > 0 ? (
              <div className={styles.filterField}>
                <span className={styles.filterLabel}>
                  <i
                    className="fa-solid fa-location-dot"
                    aria-hidden="true"
                  ></i>
                  Destination
                </span>
                <SearchableSelect
                  options={destinationOptions}
                  value={destination}
                  onChange={setDestination}
                  placeholder="All Destinations"
                />
              </div>
            ) : null}

            {themeOptions.length > 0 ? (
              <div className={styles.filterField}>
                <span className={styles.filterLabel}>
                  <i className="fa-solid fa-compass" aria-hidden="true"></i>
                  Theme
                </span>
                <SearchableSelect
                  options={themeOptions}
                  value={theme}
                  onChange={setTheme}
                  placeholder="All Themes"
                />
              </div>
            ) : null}

            {hasFilters ? (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={clearFilters}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                <span>Clear filters</span>
              </button>
            ) : null}
          </div>
        ) : null}

        <div
          className={styles.switch}
          role="group"
          aria-label="Choose how to display packages"
        >
          {VIEWS.map(({ mode, label, icon }) => (
            <button
              key={mode}
              type="button"
              className={`${styles.switchBtn} ${view === mode ? styles.active : ""}`}
              aria-pressed={view === mode}
              onClick={() => setView(mode)}
            >
              <i className={`fa-solid ${icon}`} aria-hidden="true"></i>
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Results ── */}
      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <i className="fa-regular fa-face-frown" aria-hidden="true"></i>
          <p className={styles.emptyTitle}>No packages match your filters</p>
          <p className={styles.emptyText}>
            Try a different destination or theme.
          </p>
          {hasFilters ? (
            <button
              type="button"
              className="th-btn2 style2"
              onClick={clearFilters}
            >
              <span>Clear filters</span>
            </button>
          ) : null}
        </div>
      ) : view === "list" ? (
        <div key={resultsKey} className={`row gy-4 ${styles.fade}`}>
          {filtered.map((pack) => (
            <div key={pack.id} className="col-12">
              <PackageListCard pack={pack} />
            </div>
          ))}
        </div>
      ) : (
        <div key={resultsKey} className={`${styles.grid} ${styles.fade}`}>
          {filtered.map((pack) => (
            <PackageGridCard key={pack.id} pack={pack} />
          ))}
        </div>
      )}
    </>
  );
}
